const ON_CHANGE_DEBOUNCE_TIMER = 300;

const PUB_SUB_EVENTS = {
  cartUpdate: 'cart-update',
  quantityUpdate: 'quantity-update',
  optionValueSelectionChange: 'option-value-selection-change',
  variantChange: 'variant-change',
  cartError: 'cart-error',
};

/**
 * TZN checkout safeguard
 * Enforces the 19 EUR minimum order value on every storefront checkout entry point.
 * The customer-facing minimum-order message is rendered by the existing cart UI;
 * this safeguard only blocks checkout and accelerated checkout bypasses.
 */
(() => {
  const MINIMUM_ORDER_VALUE = 1900;
  const CART_CHECKOUT_BUTTONS = '#checkout, #CartDrawer-Checkout';
  const CART_EXPRESS_CHECKOUT = '.additional-checkout-buttons';
  const PDP_EXPRESS_CHECKOUT = '.shopify-payment-button, [data-shopify="payment-button"]';

  let minimumOrderBlocked = null;
  let syncTimer;
  let syncing = false;

  function setElementHidden(element, hidden, marker) {
    if (!element) return;

    if (hidden) {
      if (!element.hasAttribute(marker)) {
        element.setAttribute(marker, 'true');
      }
      element.style.display = 'none';
      element.setAttribute('aria-hidden', 'true');
    } else if (element.getAttribute(marker) === 'true') {
      element.style.removeProperty('display');
      element.removeAttribute('aria-hidden');
      element.removeAttribute(marker);
    }
  }

  function disablePdpExpressCheckout() {
    document.querySelectorAll(PDP_EXPRESS_CHECKOUT).forEach((element) => {
      setElementHidden(element, true, 'data-tzn-pdp-express-disabled');
    });
  }

  function removeDuplicateMinimumOrderMessage() {
    document.querySelectorAll('.tzn-minimum-order-message').forEach((message) => message.remove());
  }

  function applyMinimumOrderState(cart) {
    const itemCount = Number(cart?.item_count || 0);
    const totalPrice = Number(cart?.total_price || 0);
    const isBlocked = itemCount > 0 && totalPrice < MINIMUM_ORDER_VALUE;

    minimumOrderBlocked = isBlocked;
    removeDuplicateMinimumOrderMessage();

    document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
      button.disabled = itemCount === 0 || isBlocked;

      if (isBlocked) {
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      } else {
        button.removeAttribute('data-tzn-minimum-order-blocked');
        if (itemCount > 0) button.removeAttribute('aria-disabled');
      }
    });

    document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
      setElementHidden(container, isBlocked, 'data-tzn-minimum-order-hidden');
    });

    disablePdpExpressCheckout();
  }

  async function syncMinimumOrderGuard() {
    if (syncing) return;
    syncing = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Cart request failed with status ${response.status}`);
      const cart = await response.json();
      applyMinimumOrderState(cart);
    } catch (error) {
      minimumOrderBlocked = true;
      removeDuplicateMinimumOrderMessage();
      document.querySelectorAll(CART_CHECKOUT_BUTTONS).forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.setAttribute('data-tzn-minimum-order-blocked', 'true');
      });
      document.querySelectorAll(CART_EXPRESS_CHECKOUT).forEach((container) => {
        setElementHidden(container, true, 'data-tzn-minimum-order-hidden');
      });
      disablePdpExpressCheckout();
      console.warn('TZN checkout safeguard could not refresh the cart state.', error);
    } finally {
      syncing = false;
    }
  }

  function scheduleMinimumOrderSync(delay = 80) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncMinimumOrderGuard, delay);
  }

  function shouldBlockCheckoutInteraction(target) {
    if (!target) return false;

    const cartCheckout = target.closest?.(
      `${CART_CHECKOUT_BUTTONS}, ${CART_EXPRESS_CHECKOUT}, a[href*="/checkout"]`
    );
    if (cartCheckout && minimumOrderBlocked !== false) return true;

    const pdpExpress = target.closest?.(PDP_EXPRESS_CHECKOUT);
    return Boolean(pdpExpress);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (!shouldBlockCheckoutInteraction(event.target)) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      scheduleMinimumOrderSync(0);
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      const isCartForm = form?.id === 'cart' || form?.id === 'CartDrawer-Form';
      const isCheckoutSubmit = event.submitter?.name === 'checkout';

      if (isCartForm && isCheckoutSubmit && minimumOrderBlocked !== false) {
        event.preventDefault();
        event.stopImmediatePropagation();
        scheduleMinimumOrderSync(0);
      }
    },
    true
  );

  if (document.body) {
    const cartSelector = 'cart-drawer, cart-items, cart-notification, #main-cart-footer, .cart__blocks, .shopify-payment-button, [data-shopify="payment-button"]';
    const observer = new MutationObserver(records => {
      const cartChanged = records.some(record => record.target instanceof Element && (record.target.closest(cartSelector) || [...record.addedNodes].some(node => node instanceof Element && (node.matches(cartSelector) || node.querySelector(cartSelector)))));
      if (!cartChanged) return;
      removeDuplicateMinimumOrderMessage();
      disablePdpExpressCheckout();
      scheduleMinimumOrderSync();
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        removeDuplicateMinimumOrderMessage();
        disablePdpExpressCheckout();
        scheduleMinimumOrderSync(0);
      },
      { once: true }
    );
  } else {
    removeDuplicateMinimumOrderMessage();
    disablePdpExpressCheckout();
    scheduleMinimumOrderSync(0);
  }

  window.addEventListener('pageshow', () => scheduleMinimumOrderSync(0));
})();

/**
 * TZN mobile fix for the YOYO Loyalty drawer.
 * Keeps the storefront fixed while the rewards drawer is open and lets the drawer scroll itself.
 */
(() => {
  if (window.__tznYoyoMobileScrollFix) return;
  window.__tznYoyoMobileScrollFix = true;

  const mobile = window.matchMedia('(max-width: 990px)');
  let activePanel = null;
  let activeScrollRoot = null;
  let lockedScrollY = 0;
  let bodySnapshot = null;
  let refreshQueued = false;

  const isVisible = (el) => {
    if (!el || !el.isConnected) return false;
    const style = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && rect.width > 0 && rect.height > 0;
  };

  const findTextAnchor = () => {
    const app = document.getElementById('clr-app');
    if (!app) return null;
    const nodes = app.querySelectorAll('div, section, aside, main');
    for (const node of nodes) {
      if (!isVisible(node)) continue;
      const text = (node.textContent || '').replace(/\s+/g, ' ').trim();
      if (text.includes('Mitglied werden') && text.includes('Möglichkeiten zu verdienen')) return node;
    }
    return null;
  };

  const findPanel = () => {
    const anchor = findTextAnchor();
    if (!anchor) return null;

    let node = anchor;
    let best = anchor;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    while (node && node !== document.body) {
      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);
      if (
        rect.width >= vw * 0.78 &&
        rect.height >= vh * 0.65 &&
        (style.position === 'fixed' || style.position === 'absolute')
      ) {
        best = node;
      }
      node = node.parentElement;
    }
    return best;
  };

  const findScrollRoot = (panel) => {
    const candidates = [panel, ...panel.querySelectorAll('div, section, main')];
    let best = panel;
    let overflow = Math.max(0, panel.scrollHeight - panel.clientHeight);

    for (const el of candidates) {
      if (!isVisible(el)) continue;
      const amount = Math.max(0, el.scrollHeight - el.clientHeight);
      if (amount > overflow) {
        overflow = amount;
        best = el;
      }
    }
    return best;
  };

  const lockPage = () => {
    if (bodySnapshot) return;
    lockedScrollY = window.scrollY || window.pageYOffset || 0;
    bodySnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
      htmlOverflow: document.documentElement.style.overflow
    };

    document.documentElement.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${lockedScrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
  };

  const unlockPage = () => {
    if (!bodySnapshot) return;
    const snapshot = bodySnapshot;
    bodySnapshot = null;

    document.documentElement.style.overflow = snapshot.htmlOverflow;
    document.body.style.position = snapshot.position;
    document.body.style.top = snapshot.top;
    document.body.style.left = snapshot.left;
    document.body.style.right = snapshot.right;
    document.body.style.width = snapshot.width;
    document.body.style.overflow = snapshot.overflow;
    window.scrollTo(0, lockedScrollY);
  };

  const activatePanel = (panel) => {
    if (!mobile.matches || !panel) return;
    const firstActivation = panel !== activePanel;
    activePanel = panel;
    activeScrollRoot = findScrollRoot(panel);

    panel.style.setProperty('max-height', '100dvh', 'important');
    panel.style.setProperty('overscroll-behavior-y', 'contain', 'important');

    if (activeScrollRoot) {
      activeScrollRoot.style.setProperty('overflow-y', 'auto', 'important');
      activeScrollRoot.style.setProperty('-webkit-overflow-scrolling', 'touch', 'important');
      activeScrollRoot.style.setProperty('overscroll-behavior-y', 'contain', 'important');
      activeScrollRoot.style.setProperty('touch-action', 'pan-y', 'important');
    }

    lockPage();

    if (firstActivation) {
      requestAnimationFrame(() => {
        panel.scrollTop = 0;
        if (activeScrollRoot) activeScrollRoot.scrollTop = 0;
      });
    }
  };

  const refresh = () => {
    refreshQueued = false;
    if (!document.body) return;

    if (!mobile.matches) {
      activePanel = null;
      activeScrollRoot = null;
      unlockPage();
      return;
    }

    const panel = findPanel();
    if (panel) {
      activatePanel(panel);
    } else if (activePanel) {
      activePanel = null;
      activeScrollRoot = null;
      unlockPage();
    }
  };

  const queueRefresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.setTimeout(refresh, 150);
  };

  document.addEventListener('touchmove', (event) => {
    if (!activePanel) return;
    if (!activePanel.contains(event.target)) event.preventDefault();
  }, { passive: false, capture: true });

  document.addEventListener('wheel', (event) => {
    if (!activePanel) return;
    if (!activePanel.contains(event.target)) event.preventDefault();
  }, { passive: false, capture: true });

  const init = () => {
    const observer = new MutationObserver(queueRefresh);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden']
    });

    mobile.addEventListener?.('change', queueRefresh);
    window.addEventListener('resize', queueRefresh, { passive: true });
    window.addEventListener('pageshow', queueRefresh);
    document.addEventListener('click', () => window.setTimeout(queueRefresh, 50), true);
    queueRefresh();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
